import type { CarouselPage, PageOrigin } from '@/components/ui/page-carousel';
import type { AreaMatrixColumn } from '@/components/vendor-intelligence/area-matrix';
import type { NotAssessedArea } from '@/lib/api/vendor-intelligence';
import type { EditorBlock, VendorCardData } from '@/components/report-editor';
import { fillTokens } from '@/components/report-editor';
import type {
  PublishedReportBody,
  ReportBlock,
  ReportBlockSlot,
  ReportCitation,
  ReportCover,
  ReportFit,
  ReportTables,
  VendorReport,
} from '@/lib/api/vendor-intelligence-report';
import { formatDate } from '@/lib/utils/format';
import { REFUSAL_FLAG, moduleNamer, toEditorCitation } from './report-logic';

/**
 * The report as pages (T-0283; E24-design §5 and §9; ADR-026 decision 5;
 * report-contract v2). One shape for what the carousel shows, whether it
 * is the draft being edited (the GET's `draft`, `tokens` and `tables`) or
 * a published version (its frozen body, read-only). Pure: nothing here
 * computes a figure; every number is a token or a table the server
 * composed.
 */

export interface ReportPageModel {
  key: string;
  title: string;
  /** The shortlisted vendor a `vendor:<id>` page is about. */
  product_id: string | null;
  /** The slots the page's blocks fill, with the heading printed over each (v2). */
  slots: ReportBlockSlot[];
  blocks: ReportBlock[];
}

export interface ReportModel {
  kind: 'draft' | 'published';
  version: number;
  pages: ReportPageModel[];
  tokens: Record<string, string>;
  tables: ReportTables | null;
  fit: ReportFit;
  cover: ReportCover | null;
  /** Every citation the blocks carry, by key. */
  citations: Map<string, ReportCitation>;
  /** When the fit was frozen (the draft) or the version published. */
  at: string | null;
}

/** The pages' titles when a page carries none (E24-design §5). */
export const PAGE_TITLE: Record<string, string> = {
  cover: 'Cover',
  summary: 'Executive summary',
  told_us: 'What the client told us',
  requirements: 'The requirements',
  matrix: 'Fit matrix',
  ranking: 'The ranking',
  set_aside: 'Considered and set aside',
  method: 'Method and next steps',
};

/** The headings the pages print when a page carries no slot label for the role. */
export const DEFAULT_LABEL: Record<string, string> = {
  why_these: 'Why these',
  scope_signals: 'Scope signals',
  what_changes: 'What would change the answer',
  why: 'Why it is on the shortlist',
  watch_outs: 'Watch-outs',
  scenarios: 'Prove it in the demonstration',
  sources: 'Sources',
  how_numbers: 'How the numbers are made',
  limits: 'Limits',
  next_steps: 'Next steps',
};

export const DISPOSITION_LABEL: Record<string, string> = {
  shortlisted: 'Shortlisted',
  alternate: 'Alternate',
  set_aside: 'Set aside',
  not_assessed: 'Not assessed',
};

/** The sentence on the summary page's foot when the needs page is left out (the contract's `NEEDS_OMITTED_NOTE`). */
export const NO_NEEDS_PAGE_NOTE =
  'Discovery evidence is not yet confirmed in Caliber for this engagement.';

export function isVendorPage(key: string): boolean {
  return key.startsWith('vendor:');
}

function titleOf(key: string, title: string): string {
  return title.trim() || PAGE_TITLE[key] || key;
}

/** The draft as pages: its `pages`, or (a v1 draft) its sections shown as pages. */
export function modelFromReport(report: VendorReport): ReportModel | null {
  const draft = report.draft;
  if (!draft) return null;
  const groups = draft.pages ?? draft.sections ?? [];
  const citations = new Map<string, ReportCitation>();
  for (const g of groups)
    for (const b of g.blocks) for (const c of b.citations) citations.set(c.key, c);
  return {
    kind: 'draft',
    version: draft.version,
    pages: groups.map((g) => ({
      key: g.key,
      title: titleOf(g.key, g.title),
      product_id:
        'product_id' in g && typeof g.product_id === 'string'
          ? g.product_id
          : isVendorPage(g.key)
            ? g.key.slice('vendor:'.length)
            : null,
      slots: 'slots' in g && g.slots ? g.slots : [],
      blocks: g.blocks,
    })),
    tokens: report.tokens,
    tables: report.tables,
    fit: draft.fit_result,
    cover: draft.cover ?? null,
    citations,
    at: draft.drafted_at,
  };
}

/** A published version's frozen body as pages, read-only: tokens are already filled in its text. */
export function modelFromPublished(body: PublishedReportBody, version: number): ReportModel {
  const byKey = new Map<string, ReportCitation>();
  for (const c of body.citations ?? []) {
    byKey.set(c.key, {
      key: c.key,
      kind: c.kind ?? 'vendor_knowledge_item',
      ref_id: c.ref_id ?? c.key,
      code: c.code ?? null,
      display: c.display ?? null,
      ...(c.rating_items ? { rating_items: c.rating_items } : {}),
      label: c.label ?? c.display ?? c.code ?? c.key,
      excerpt: c.excerpt ?? null,
      statement: c.statement ?? null,
      source_label: c.source_label ?? '',
      date: c.date ?? null,
      url: c.url ?? null,
      locator_label: c.locator_label ?? null,
      confidential: c.confidential ?? false,
      product_id: c.product_id ?? null,
      module_id: c.module_id ?? null,
    });
  }
  const groups = body.pages ?? body.sections ?? [];
  const pages: ReportPageModel[] = groups.map((g) => ({
    key: g.key,
    title: titleOf(g.key, g.title),
    product_id: g.product_id ?? (isVendorPage(g.key) ? g.key.slice('vendor:'.length) : null),
    slots: g.slots ?? [],
    blocks: g.blocks.map((b, i): ReportBlock => {
      // A list block's per-line keys become each citation's `lines`.
      const linesOf = new Map<string, number[]>();
      (b.line_citation_keys ?? []).forEach((keys, at) => {
        for (const k of keys) linesOf.set(k, [...(linesOf.get(k) ?? []), at + 1]);
      });
      return {
        id: b.id,
        page_key: g.key,
        position: b.position ?? i + 1,
        product_id: b.product_id ?? null,
        kind: b.kind,
        role: b.role ?? null,
        ai_text: b.text,
        text: b.text,
        display_text: b.text,
        origin: b.origin ?? 'ai_drafted',
        citations: (b.citation_keys ?? [])
          .map((k) => {
            const c = byKey.get(k);
            if (!c) return undefined;
            const lines = linesOf.get(k);
            return lines ? { ...c, lines } : c;
          })
          .filter((c): c is ReportCitation => c !== undefined),
        is_safe_refusal: false,
        issues: [],
        last_instruction: null,
        edited_by_user_id: null,
        edited_at: null,
      };
    }),
  }));
  return {
    kind: 'published',
    version: body.version ?? version,
    pages,
    tokens: body.tokens,
    tables: body.tables ?? null,
    fit: body.fit_result,
    cover: body.cover ?? null,
    citations: byKey,
    at: body.cover?.published_at ?? body.captured_at ?? null,
  };
}

export type FitProduct = ReportFit['products'][number];

export function productOf(model: ReportModel, productId: string | null): FitProduct | undefined {
  if (productId === null) return undefined;
  return model.fit.products.find((p) => p.product_id === productId);
}

/** Who wrote a page's prose, for the strip's dot: edited beats AI; a page with no prose is code. */
export function pageOrigin(page: ReportPageModel): PageOrigin {
  let ai = false;
  for (const b of page.blocks) {
    if (b.origin === 'consultant_edited' || b.origin === 'ai_rewritten') return 'edited';
    if (b.origin === 'ai_drafted') ai = true;
  }
  return ai ? 'ai' : 'code';
}

/** The page's open issues: the prose checks, plus a block the AI could not draft. */
export function pageIssues(page: ReportPageModel): number {
  return page.blocks.reduce((n, b) => n + b.issues.length + (b.is_safe_refusal ? 1 : 0), 0);
}

/** The strip's title: a vendor page reads as its vendor, the rest as the page. */
export function stripTitle(model: ReportModel, page: ReportPageModel): string {
  if (!isVendorPage(page.key)) return page.title;
  return productOf(model, page.product_id)?.name ?? page.title;
}

/** The strip's pages: number and title, the origin dot and the issue count (E24-design §9). */
export function carouselPages(model: ReportModel): CarouselPage[] {
  return model.pages.map((p) => {
    const issues = pageIssues(p);
    return {
      key: p.key,
      title: stripTitle(model, p),
      origin: pageOrigin(p),
      ...(issues > 0 ? { issues } : {}),
    };
  });
}

/** "Shortlist · 2 of 3" for a vendor page; the page's title otherwise. */
export function pageEyebrow(model: ReportModel, page: ReportPageModel): string {
  if (!isVendorPage(page.key)) return page.title;
  const vendors = model.pages.filter((p) => isVendorPage(p.key));
  const at = vendors.findIndex((p) => p.key === page.key);
  return `Shortlist · ${at + 1} of ${vendors.length}`;
}

/**
 * The blocks of a role on a page: the ones that carry it; when no block on
 * the page carries a role (a draft prepared before roles), the fallback's
 * pick by kind and order.
 */
export function blocksOfRole(
  page: ReportPageModel,
  role: string,
  fallback: (blocks: ReportBlock[]) => ReportBlock[],
): ReportBlock[] {
  const roled = page.blocks.some((b) => typeof b.role === 'string' && b.role !== '');
  return roled ? page.blocks.filter((b) => b.role === role) : fallback(page.blocks);
}

/** The heading a slot prints over its role, its tokens filled; the default when a token is unanswered. */
export function slotLabel(model: ReportModel, page: ReportPageModel, role: string): string {
  const label = page.slots.find((s) => s.role === role)?.label ?? '';
  if (label === '') return DEFAULT_LABEL[role] ?? '';
  const words = fillTokens(label, model.tokens);
  return words.includes('—') ? (DEFAULT_LABEL[role] ?? label) : words;
}

/** The product's token number (`vendor.N`): its place in the fit's product order. */
export function tokenNumber(model: ReportModel, productId: string): number | null {
  const i = model.fit.products.findIndex((p) => p.product_id === productId);
  return i < 0 ? null : i + 1;
}

/** A vendor card's code-composed parts: the name, the rank, and its two figures by token. */
export function cardData(model: ReportModel, block: ReportBlock): VendorCardData | null {
  if (block.kind !== 'vendor_card' || !block.product_id) return null;
  const product = productOf(model, block.product_id);
  const n = tokenNumber(model, block.product_id);
  return {
    name: product?.name ?? 'Vendor',
    rank: product?.rank ?? null,
    figures:
      n === null
        ? []
        : [
            { label: 'Index', token: `vendor.${n}.index` },
            { label: 'No evidence', token: `vendor.${n}.no_evidence` },
          ],
  };
}

/** The dispositions the report froze: the cover's, else the fit's. */
export function dispositionsOf(model: ReportModel) {
  const fromCover = model.cover?.dispositions ?? [];
  return fromCover.length > 0 ? fromCover : (model.fit.dispositions ?? []);
}

export function dispositionOf(model: ReportModel, productId: string): string | null {
  return dispositionsOf(model).find((d) => d.product_id === productId)?.disposition ?? null;
}

/** Shortlisted by disposition; by the fit's top three when the report has no dispositions. */
export function isShortlisted(model: ReportModel, productId: string): boolean {
  const d = dispositionOf(model, productId);
  if (d !== null) return d === 'shortlisted';
  const p = productOf(model, productId);
  return p?.top === true || (model.fit.top ?? []).includes(productId);
}

/** "Rank 2 · Fit index 56.9 · evidence coverage 90.6%": the line under a vendor page's name. */
export function rankLine(product: FitProduct | undefined): string | null {
  if (!product) return null;
  const rank =
    product.rank === null
      ? ''
      : `${product.tied_with && product.tied_with.length > 0 ? 'Joint rank' : 'Rank'} ${product.rank} · `;
  return `${rank}Fit index ${product.index.toFixed(1)} · evidence coverage ${product.coverage.toFixed(1)}%`;
}

export interface CoverTile {
  label: string;
  value: string;
  note?: string;
}

/** The cover's three tiles (ADR-026 decision 8), from the cover facts and the frozen fit; the go-live tile only with a planned end. */
export function coverTiles(model: ReportModel): CoverTile[] {
  const tiles: CoverTile[] = [];
  const shortlisted =
    model.cover?.shortlist?.length ??
    dispositionsOf(model).filter((d) => d.disposition === 'shortlisted').length;
  const assessed =
    model.cover?.assessed_count ?? model.fit.products.filter((p) => p.rank !== null).length;
  tiles.push({
    label: 'Shortlist',
    value: `${shortlisted} ${shortlisted === 1 ? 'vendor' : 'vendors'}`,
    note: `of ${assessed} assessed in depth`,
  });
  const req = model.fit.requirements;
  const weighted = model.cover?.requirements_weighted ?? req?.weighted ?? null;
  const critical = model.cover?.requirements_critical ?? req?.critical;
  const desired = model.cover?.requirements_desired ?? req?.desired;
  if (weighted !== null) {
    const parts = [
      typeof critical === 'number' ? `${critical} critical` : '',
      typeof desired === 'number' ? `${desired} desired` : '',
      req && req.not_set > 0 ? `${req.not_set} not set` : '',
    ].filter((p) => p !== '');
    tiles.push({
      label: 'Requirements weighed',
      value: String(weighted),
      ...(parts.length > 0 ? { note: parts.join(' · ') } : {}),
    });
  }
  if (model.cover?.planned_end) {
    tiles.push({ label: 'Target go-live', value: formatDate(model.cover.planned_end) });
  }
  return tiles;
}

/** "City of Everett, MA · Project AUD-27-06": the cover's client line. */
export function coverClientLine(
  model: ReportModel,
  engagement?: { client_name: string; name: string } | null,
): string {
  const client = model.cover?.client_name ?? engagement?.client_name ?? '';
  const name = model.cover?.engagement_name ?? engagement?.name ?? '';
  return [client, name].filter((p) => p !== '').join(' · ');
}

/** Whether the needs page prints: it is among the pages and its table has rows. */
export function hasNeedsPage(model: ReportModel): boolean {
  return (
    model.pages.some((p) => p.key === 'told_us') && (model.tables?.needs?.rows.length ?? 0) > 0
  );
}

/** The report's blocks for the editor, with the card data a vendor card needs and each citation's lines. */
export function toEditorBlocks(model: ReportModel, blocks: ReportBlock[]): EditorBlock[] {
  const moduleName = moduleNamer(model.fit);
  return blocks.map((b) => ({
    id: b.id,
    kind: b.kind,
    text: b.text,
    origin: b.origin,
    citations: b.citations.map((c) => ({
      ...toEditorCitation(c, moduleName),
      kind: c.kind,
      ...(c.lines ? { lines: c.lines } : {}),
    })),
    lastInstruction: b.last_instruction,
    editedAt: b.edited_at,
    flag: b.is_safe_refusal ? REFUSAL_FLAG : null,
    group: null,
    card: cardData(model, b),
  }));
}

/** The matrix prints at most this many vendors (E24-design §5). */
export const MATRIX_MAX_VENDORS = 8;

/**
 * The matrix's columns for the Shortlist step's `AreaMatrix`: the vendors
 * assessed in depth (shortlisted and alternates), at most eight, from the
 * frozen `area_matrix` table or, for a draft prepared before it, from the
 * frozen fit and the dispositions. `rank` is the mark the table prints
 * ("2=" for a shared rank).
 */
export function matrixColumns(model: ReportModel): AreaMatrixColumn[] {
  const frozen = model.tables?.area_matrix?.products ?? [];
  if (frozen.length > 0) {
    return frozen.slice(0, MATRIX_MAX_VENDORS).map((p) => ({
      product_id: p.product_id,
      name: p.name,
      rank: p.rank === null ? null : String(p.rank),
      index: p.index,
      coverage: p.coverage,
    }));
  }
  return model.fit.products
    .filter((p) => {
      const d = dispositionOf(model, p.product_id);
      return d === null ? p.rank !== null : d === 'shortlisted' || d === 'alternate';
    })
    .slice(0, MATRIX_MAX_VENDORS)
    .map((p) => ({
      product_id: p.product_id,
      name: p.name,
      rank: p.rank === null ? null : `${p.rank}${(p.tied_with ?? []).length > 0 ? '=' : ''}`,
      index: p.index,
      coverage: p.coverage,
    }));
}

/** The areas whose requirements have no module, as the matrix lists them under the table. */
export function notAssessedAreas(model: ReportModel): NotAssessedArea[] {
  return (model.fit.not_assessed?.areas ?? []).map((a) => {
    const name = a.area ?? 'Other';
    return {
      key:
        name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'area',
      name,
      requirements: a.requirements,
      critical: a.critical,
    };
  });
}
